import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { cx } from 'linaria';
import {
  ModalContent,
  ModalFooter,
  ModalHeader,
  Portal,
  RegularButton,
  Typography,
  Divider,
} from 'fronton-react';
import { Modal } from 'fronton-react/modal';
import { InfoIcon } from '@fronton/icons-react';
import { analyticsUtils } from 'utils';
import { Skeleton } from 'common/components/skeleton';
import { GET_PRODUCT_MERCHANT_INFO } from 'features/product-info/ducks/product-merchant-info';
import { getMerchantInfo, getMerchantError } from 'features/product-info/ducks/selectors';
import { getRegionId } from 'features/page-model/ducks/get-model';
import { getIsEcommerce } from 'features/goods-supply';
import {
  container,
  labelWrapper,
  iconButtonStyle,
  divider,
  skeleton,
  skeletonShort,
  merchantInfoHeading,
  modalContent,
  onlineOrderOnlyMessageTextStyle,
} from './styles';
import { Error } from './error';

export const SKELETON_ROWS = 4;
export const DATA_QA_TIME_IN_STORE_CAPTION = 'online_order_only_message_in_store_caption';

export const OnlineOrderOnlyMessage = () => {
  const dispatch = useDispatch();
  const [modalVisibility, setModalVisibility] = useState<boolean>(false);
  const [errorKey, setErrorKey] = useState(0);
  const merchantInfo = useSelector(getMerchantInfo);
  const isEcommerce = useSelector(getIsEcommerce);
  const error = useSelector(getMerchantError);
  const regionId = useSelector(getRegionId) || '';
  const { t } = useTranslation();

  const openModal = () => {
    analyticsUtils.sendEvent('GA:clickButton', {
      elementType: 'button',
      elementBlock: 'productDetail',
      elementName: 'onlineOrder3P',
      elementText: t('product-info.online-order-only.title'),
    });
    setModalVisibility(true);
    if (merchantInfo) return;
    dispatch(GET_PRODUCT_MERCHANT_INFO({ regionId }));
  };

  useEffect(() => {
    setErrorKey(errorKey + 1);
  }, [error]);

  const renderSkeleton = () =>
    Array(SKELETON_ROWS)
      .fill('')
      .map((_, index) => (
        <Skeleton
          key={index}
          className={cx(skeleton, index % 2 ? null : skeletonShort)}
          blink={true}
          height={'10px'}
        />
      ));

  return (
    <>
      <div className={container} data-qa="online-order-only-message">
        <div className={labelWrapper}>
          <Typography
            variant="s"
            size="body_accent"
            color="secondary-blue-primary"
            className={onlineOrderOnlyMessageTextStyle}
            data-qa="online-order-only-message-text"
          >
            {t('product-info.online-order-only.title')}
          </Typography>
          {!isEcommerce && (
            <InfoIcon
              color="control-primary"
              onClick={openModal}
              data-qa="info-icon"
              size="l"
              className={iconButtonStyle}
            />
          )}
        </div>
        <Typography variant="caption" color="text-primary">
          {t('product-info.online-order-only.part1')}
        </Typography>
        <Portal>
          <Modal
            show={modalVisibility}
            modalID="merchant-info"
            onClose={() => setModalVisibility(false)}
            size="s"
            variant="curtain"
          >
            <ModalHeader title={t('product-info.online-order-only.title')} />
            <ModalContent className={modalContent}>
              <Typography variant="m" size="body_long">
                {t('product-info.online-order-only.part2')}
              </Typography>
              <Divider className={divider} />
              <Typography
                as="p"
                variant="minor"
                size="overline"
                color="text-secondary"
                className={merchantInfoHeading}
              >
                {t('product-info.online-order-only.part3')}
              </Typography>
              {merchantInfo ? (
                <>
                  {merchantInfo?.name && (
                    <Typography as="p" variant="caption" color="text-secondary">
                      {merchantInfo.name}
                    </Typography>
                  )}
                  {merchantInfo?.address && (
                    <Typography as="p" variant="caption" color="text-secondary">
                      {merchantInfo.address}
                    </Typography>
                  )}
                  {merchantInfo?.legalRegistrationNumber && (
                    <Typography as="p" variant="caption" color="text-secondary">
                      {merchantInfo.legalRegistrationNumber}
                    </Typography>
                  )}
                  <Typography
                    as="p"
                    variant="caption"
                    color="text-secondary"
                    data-qa={DATA_QA_TIME_IN_STORE_CAPTION}
                  >
                    {t('product-info.online-order-only.part4', {
                      brand: 'Лемана ПРО',
                    })}
                  </Typography>
                </>
              ) : (
                renderSkeleton()
              )}
            </ModalContent>
            <ModalFooter>
              <RegularButton
                aria-label={t('product-info.online-order-only.got-it')}
                onClick={() => setModalVisibility(false)}
                size="l"
                variant="secondary"
                wide
              >
                {t('product-info.online-order-only.got-it')}
              </RegularButton>
            </ModalFooter>
          </Modal>
        </Portal>
      </div>
      {error && <Error key={errorKey} />}
    </>
  );
};
